"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, CircleCheck as CheckCircle2, Clock, CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, Star, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const trustBadges = [{
  title: "Factory-Trained Techs",
  desc: "Certified for Rheem tank & tankless models"
}, {
  title: "15+ Years Rheem Experience",
  desc: "Over 2,400 water heaters repaired & installed"
}, {
  title: "OEM Factory Parts",
  desc: "Direct-from-manufacturer replacement components"
}, {
  title: "Warranty Protection",
  desc: "Full labor warranty plus Rheem equipment coverage"
}];
const faqs = [{
  question: "How do I know if my Rheem water heater needs repair or replacement?",
  answer: "Common indicators that require emergency service include water leaking around the tank base, unusual rumbling or banging noises, rusty or discolored hot water, and intermittent heating. If your Rheem unit is under 10 years old, a component fix (such as a heating element, thermocouple, or gas control valve) is often sufficient. If the tank is leaking or over 10-12 years old, full replacement is the safest and most cost-effective option.",
  tag: "Troubleshooting"
}, {
  question: "Do you use genuine OEM parts for Rheem water heater repairs?",
  answer: "Yes. We stock our service trucks with authentic Rheem OEM elements, thermostats, gas valves, dip tubes, and burner assemblies to ensure full manufacturer compliance, warranty preservation, and reliable long-term performance."
}, {
  question: "Can you service both Rheem tankless and standard tank water heaters?",
  answer: "Yes. Our plumbers are trained on all Rheem equipment lines, including Rheem Performance, Performance Plus, Gladiator smart electric units, and Rheem high-efficiency tankless systems.",
  tag: "Models"
}, {
  question: "How quickly can an emergency technician arrive in Jonesboro, GA?",
  answer: "We operate 24 hours a day, 7 days a week. For emergency water heater leaks or sudden loss of hot water in Jonesboro and neighboring areas, our typical on-site arrival window is under 60 minutes.",
  tag: "Emergency"
}, {
  question: "Will repairing my water heater void my Rheem manufacturer warranty?",
  answer: "No. As licensed professional plumbers using genuine parts, our diagnostic and repair procedures adhere strictly to Rheem installation guidelines, keeping your factory warranty intact.",
  tag: "Warranty"
}, {
  question: "What should I do while waiting for the plumber to arrive?",
  answer: "Immediately turn off the cold water shutoff valve above your water heater. For electric models, flip the corresponding breaker in your electrical panel. For gas models, turn the gas valve knob to 'OFF' or 'PILOT'. This minimizes water damage risk and prevents further component damage.",
  tag: "Safety"
}];
const testimonials = [{
  name: "Marcus T.",
  location: "Jonesboro, GA",
  review: "Our Rheem 50-gallon electric heater stopped heating on a Sunday morning. The Jonesboro team arrived in 40 minutes, replaced the upper element on the spot, and charged exactly what they quoted upfront.",
  model: "Rheem Performance 50-Gal",
  date: "2 weeks ago"
}, {
  name: "Elena R.",
  location: "Riverdale, GA",
  review: "Had a leaking pressure relief valve on our Rheem tankless unit. The technician was certified, extremely polite, and had the exact Rheem OEM replacement valve in his truck.",
  model: "Rheem Tankless Service",
  date: "1 month ago"
}, {
  name: "David H.",
  location: "Morrow, GA",
  review: "Outstanding emergency service. Replaced our 12-year-old failing heater with a new Rheem Gladiator model. Fast cleanup, smooth inspection, and great warranty coverage.",
  model: "Rheem Gladiator Install",
  date: "2 months ago"
}];
export default function RheemFaq() {
  const prefersReduced = useReducedMotion();
  const [activeTab, setActiveTab] = useState<string>("all");
  const filteredFaqs = activeTab === "all" ? faqs : faqs.filter(faq => faq.tag?.toLowerCase() === activeTab.toLowerCase());
  return <section id="rheemfaqandtrust" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-3">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Rheem Certified Water Heater Specialists</span>
          </div>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Rheem Water Heater FAQ & Trust
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default">
            Clear answers about Rheem water heater diagnostics, OEM parts, warranties, and emergency repairs backed by over 15 years of local hands-on experience in Jonesboro, GA.
          </p>
        </div>

        {/* Trust Badges / Credentials Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {trustBadges.map((badge, i) => <div key={badge.title} data-index={i} className="bg-card text-card-foreground border border-border rounded-xl p-5 shadow-xs flex flex-col justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                  {i === 0 && <Award className="size-5" />}
                  {i === 1 && <Clock className="size-5" />}
                  {i === 2 && <Wrench className="size-5" />}
                  {i === 3 && <ShieldCheck className="size-5" />}
                </div>
                <div>
                  <h3 className="font-default text-base font-semibold text-foreground">
                    {badge.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {badge.desc}
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-border/60 flex items-center gap-1.5 text-xs text-primary font-medium">
                <CheckCircle2 className="size-3.5" />
                <span>Verified Standard</span>
              </div>
            </div>)}
        </div>

        {/* Main Content Layout: FAQ on Left, Side Trust Card & Testimonials on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* FAQ Column (7 cols) */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                  <h3 className="font-default text-2xl font-bold text-foreground">
                    Frequently Asked Questions
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Everything you need to know before our technician arrives
                  </p>
                </div>
              </div>

              {/* FAQ Accordion */}
              <Accordion type="single" collapsible defaultValue="item-0" className="w-full space-y-3">
                {filteredFaqs.map((faq, i) => <AccordionItem key={faq.question} value={`item-${i}`} data-index={i} className="border border-border rounded-lg bg-card px-4 py-1 data-[state=open]:shadow-xs transition-shadow">
                    <AccordionTrigger className="font-default text-base font-semibold text-foreground hover:no-underline py-3.5 text-left">
                      <span className="flex items-center gap-2">
                        <HelpCircle className="size-4 text-primary shrink-0" />
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed pt-1 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            {/* Support CTA Callout */}
            <div className="mt-8 p-5 bg-secondary text-secondary-foreground rounded-xl border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-sm text-foreground">
                  Need immediate Rheem assistance?
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Our emergency dispatchers are available 24 hours a day.
                </p>
              </div>
              <Button asChild size="sm" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/contact/">
                  <PhoneCall className="size-3.5 mr-1.5" />
                  Request Rheem Service
                </Link>
              </Button>
            </div>
          </div>

          {/* Trust Panel & Reviews Column (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            {/* Certification Highlight Card */}
            <Card className="border border-border bg-card text-card-foreground shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs font-semibold border-primary/30 text-primary">
                    Certified Technicians
                  </Badge>
                  <div className="flex items-center gap-1 text-accent">
                    {[0, 1, 2, 3, 4].map((_, idx) => <Star key={idx} className="size-3.5 fill-accent text-accent" />)}
                  </div>
                </div>
                <CardTitle className="font-default text-xl font-bold mt-2 text-foreground">
                  Why Trust Us With Your Rheem System
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Dedicated diagnostic protocol for safe and code-compliant repairs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3.5 text-xs text-muted-foreground">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground">Factory Diagnostic Equipment:</strong> Rapid error-code identification for modern digital & smart Rheem units.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground">Full Code Compliance:</strong> Permitted installations complying with all Clayton County plumbing codes.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <p>
                    <strong className="text-foreground">Upfront Pricing:</strong> Fixed rates with no hidden dispatch charges or surprise fees.
                  </p>
                </div>

                <Separator className="my-3" />

                {/* Testimonials mini list */}
                <div>
                  <h4 className="font-default text-xs uppercase tracking-wider font-bold text-foreground mb-3">
                    Recent Customer Experiences
                  </h4>
                  <div className="space-y-3">
                    {testimonials.map((t, index) => <div key={t.name} data-index={index} className="p-3 rounded-lg bg-background border border-border/80">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <SafeHydrate><Avatar className="size-6 border border-border">
                              <AvatarFallback className="text-[10px] bg-primary/10 text-primary font-bold">
                                {t.name.slice(0, 1)}
                              </AvatarFallback>
                            </Avatar></SafeHydrate>
                            <span className="font-semibold text-foreground text-xs">{t.name}</span>
                            <span className="text-[10px] text-muted-foreground font-normal">({t.location})</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground">{t.date}</span>
                        </div>
                        <p className="text-xs text-foreground/80 leading-relaxed italic mt-1">
                          "{t.review}"
                        </p>
                        <div className="mt-2 flex items-center justify-between text-[11px] text-primary font-medium">
                          <span>{t.model}</span>
                          <span className="flex items-center gap-0.5 text-accent">
                            <Star className="size-2.5 fill-accent text-accent" />
                            5.0
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}